import React, {
  memo, useEffect, useState, useRef, useCallback, useContext,
} from 'react';
import PropTypes from 'prop-types';
import Map, {
  Popup,
} from 'react-map-gl/mapbox';
import { fitBounds } from '@math.gl/web-mercator';
import { useLocation } from 'react-router';
import useSupercluster from 'use-supercluster';
import ReactGA from 'react-ga4';
import { useMediaQuery } from 'react-responsive';
import _ from 'lodash';
import formatcoords from 'formatcoords';
import moment from 'moment';
import { useAuthenticator } from '@aws-amplify/ui-react';

import appConfig, { brandingConfig } from '../../config/index';
import DatasetSummary from './components/DatasetSummary';
import OmicsDatasetSummary from './components/OmicsDatasetSummary';
import ESPDatasetSummary from './components/ESPDatasetSummary';
import DatasetDetail from './components/DatasetDetail';
import { getSamplingData } from '../../services/dataset.service';
import {
  getParameters,
  preferredWindMagnitudeUnit,
  preferredWaterMagnitudeUnit,
  preferredTemperatureUnit,
  preferredWaveHeightUnit,
  platformGroupings,
  espStandardNames,
  getExternalParameterObservations,
} from '../../utils';
import TimeSlider from './components/TimeSlider';
import filePathFromDate from './components/FilePathFromDate';
import SurfaceWaterTemperature from './components/SurfaceWaterTemperature';
import CurrentAnimation from './components/CurrentAnimation';
import WaterMagnitude from './components/WaterMagnitude';
import WindMagnitude from './components/WindMagnitude';
import WaveMagnitude from './components/WaveMagnitude';
import WavePeriod from './components/WavePeriod';
import MapOverlayDrawer from './components/map-overlay-drawer';
import { ForecastEvent, MapFilterEvent, PlatformMarkerEvent } from '../../constant/google-analytics/constant';
import Cluster from './components/Cluster';
import Trajectory from './components/Trajectory';
import UserContext from '../../contexts/UserContext';
import CSBHexMap from './components/CSBHexMap';
import { featureFlag } from '../../constant';
import './styles.scss';
import SelectedFilters from './components/SelectedFilters';
import CustomModal from '../modal';
import MapShareModalContent from './components/MapShareModalContent';
import { mapStyle } from './constant';
import CommunitySignupModal from '../../pages/data-console/components/CommunitySignupModal';
import WebCams from './components/WebCams';
import ExternalDatasetSummary from './components/ExternalDatasetSummary';

const startingUTCPath = filePathFromDate(new Date());

const CustomMap = (props) => {
  const location = useLocation();
  const mapRef = useRef();
  const isMobile = useMediaQuery({ maxWidth: 767 });

  const DEFAULT_VIEWPORT = location.pathname === '/landing' ? {
    latitude: isMobile ? brandingConfig.map.landingLatitudeMobile : brandingConfig.map.landingLatitude,
    longitude: isMobile ? brandingConfig.map.landingLongitudeMobile : brandingConfig.map.landingLongitude,
    zoom: brandingConfig.map.landingZoom,
  } : {
    latitude: brandingConfig.map.latitude,
    longitude: brandingConfig.map.longitude,
    zoom: isMobile ? brandingConfig.map.zoomMobile : brandingConfig.map.zoom,
  };

  const {
    children,
    datasets,
    filterVisible,
    selectedLake,
    selectedOverlay,
    selectedTags,
    datasetSummaries,
    registeredParameters,
    observationalPlatforms,
    organizations,
    obsData,
    parameterConfigurations,
    pushToHistory,
    onTagSelect,
    tagsList,
    filterFavorites,
    favoritePlatformData,
    datasetTrajectories,
    showPaths,
    omicsFilters,
    setOmicsFilters,
    obsDatasetsLatest,
    showShareModal,
    setShowShareModal,
    externalObsDatasets,
    externalParameters,
    externalObsLatest,
    showWebcams,
  } = props;

  const [buoyData, setBuoyData] = useState([]);
  const [samplingLocationData, setSamplingLocationData] = useState([]);
  const [parameters, setParameters] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [tooltipInfo, setTooltipInfo] = useState(null);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [contourInfo, setContourInfo] = useState(null);
  const [clickableContourLayers, setClickableContourLayers] = useState([]);
  const [activeUTCPath, setActiveUTCPath] = useState(startingUTCPath);
  const [urlCoords, setUrlCoords] = useState('');
  const [viewport, setViewport] = useState(() => {
    const getCoordsFromSearch = (search) => {
      const coordsParam = new URLSearchParams(search).get('coords');
      if (coordsParam) {
        const [lng, lat, zoom] = coordsParam.split(',');
        if (lng && lat) {
          return {
            latitude: Number(lat),
            longitude: Number(lng),
            zoom: zoom ? Number(zoom) : DEFAULT_VIEWPORT.zoom,
          };
        }
      }
      return null;
    };
    return (
      getCoordsFromSearch(window.location.search)
      ?? getCoordsFromSearch((sessionStorage.getItem('lastMapUrl') || '').split('?')[1] || '')
      ?? DEFAULT_VIEWPORT
    );
  });
  const [modalKey, setModalKey] = useState(0);
  const [hexRes, setHexRes] = useState('5');
  const [hexSurveyCount, setHexSurveyCount] = useState(0);
  const [hexSurveys, setHexSurveys] = useState([]);
  const [clickedHexLoc, setClickedHexLoc] = useState({});
  const [showSignupModal, setShowSignupModal] = useState(false);
  const prevSelectedLakeRef = useRef(selectedLake);
  // true when the lake just changed and we're waiting for filtered platforms to arrive
  const lakePendingSnapRef = useRef(selectedLake !== '');

  const hasUserSeenShareModal = sessionStorage.getItem('hasUserSeenShareModal');

  useEffect(() => {
    setModalKey((prevKey) => prevKey + 1);
  }, [showShareModal]);

  const { unitPreferences } = useContext(UserContext);
  const { authStatus } = useAuthenticator((ctx) => [ctx.authStatus]);

  const updateUrlCoords = useCallback(
    _.debounce((coords) => {
      pushToHistory('coords', coords);
      setUrlCoords(coords);
    },
    100),
    [],
  );

  const updateUrlPlatform = useCallback(
    _.debounce((platform) => {
      pushToHistory('platform', platform);
    },
    100),
    [],
  );

  useEffect(() => () => {
    updateUrlCoords.cancel();
    updateUrlPlatform.cancel();
  }, []);

  useEffect(() => {
    sessionStorage.setItem('lastMapUrl', location.pathname + location.search);
  }, [location.search]);

  const getDms = (latitude, longitude) => {
    const decCoords = formatcoords(latitude, longitude).format(
      'ddX', { latLonSeparator: ', ', decimalPlaces: 3 },
    );
    const dmsCoords = formatcoords(latitude, longitude).format(
      'DDMMssX', { latLonSeparator: ', ', decimalPlaces: 0 },
    );

    return (
      <span>
        {decCoords}
        <br />
        &#x28;
        {dmsCoords}
        &#x29;
      </span>
    );
  };

  // const getBoundsForPoints = (points) => {
  //   // Calculate corner values of bounds
  //   if (points.length === 0) {
  //     return null;
  //   }
  //   const pointsLong = points.map((point) => point.coordinates[0]);
  //   const pointsLat = points.map((point) => point.coordinates[1]);
  //   const cornersLongLat = [
  //     [Math.min(...pointsLong), Math.min(...pointsLat)],
  //     [Math.max(...pointsLong), Math.max(...pointsLat)],
  //   ];
  //   // Use WebMercatorViewport to get center longitude/latitude and zoom
  //   const viewport = new WebMercatorViewport({
  //     width: 1000,
  //     height: 600,
  //   }).fitBounds(cornersLongLat, { padding: 50 }); // Can also use option: offset: [0, -100]
  //   const {
  //     longitude,
  //     latitude,
  //   } = viewport;
  //   return {
  //     longitude,
  //     latitude,
  //     zoom: DEFAULT_VIEWPORT.zoom,
  //   };
  // };

  const getBoundsForPoints = (points, { width = 1000, height = 600, padding = 50 } = {}) => {
    if (!points || points.length === 0) return null;

    const lngs = points.map((p) => p.coordinates[0]);
    const lats = points.map((p) => p.coordinates[1]);
    const bounds = [
      [Math.min(...lngs), Math.min(...lats)],
      [Math.max(...lngs), Math.max(...lats)],
    ];

    const { longitude, latitude, zoom } = fitBounds({
      width,
      height,
      bounds,
      padding,
    });

    return {
      longitude, latitude, zoom, bounds,
    };
  };

  const handleSliderChange = useCallback(
    _.debounce((newUTCPath) => {
      setActiveUTCPath(newUTCPath);
    },
    500),
    [],
  );

  useEffect(() => {
    const longitude = viewport.longitude.toFixed(7);
    const latitude = viewport.latitude.toFixed(7);
    const zoom = String(Math.round(viewport.zoom * 10) / 10);

    if (location.pathname.includes('map')) {
      updateUrlCoords(encodeURI(`${longitude},${latitude},${zoom}`));

      updateUrlPlatform(selectedPlatform?.properties?.org_platform_id);
    }

    const hexResTemp = Math.floor(viewport.zoom);

    if (hexResTemp <= 5) {
      setHexRes('5');
    } else if (hexResTemp === 6) {
      setHexRes('6');
    } else if (hexResTemp === 7 || hexResTemp === 8) {
      setHexRes('7');
    } else if (hexResTemp === 9 || hexResTemp === 10) {
      setHexRes('8');
    } else if (hexResTemp === 11 || hexResTemp === 12) {
      setHexRes('9');
    } else if (hexResTemp >= 13) {
      setHexRes('10');
    }
  }, [selectedPlatform, viewport]);

  useEffect(() => {
    switch (selectedOverlay) {
      case 'surface_water_temperature':
        ReactGA.event(ForecastEvent.WaterTempOn);
        break;
      case 'water_current':
        ReactGA.event(ForecastEvent.WaterCurrentOn);
        break;
      default:
        break;
    }
  }, [selectedOverlay]);

  // TODO: clean this up
  useEffect(() => {
    if (location.pathname === '/landing' && !location.search.includes('coords')) {
      // setViewport({
      //   latitude: 44.858323,
      //   longitude: -89.131259,
      //   zoom: 5.5,
      // });
      setViewport(DEFAULT_VIEWPORT);
    }
    if (location.pathname === '/map' && !location.search.includes('coords') && !selectedPlatform) {
      // setViewport({
      //   latitude: 44.149,
      //   longitude: -84.131259,
      //   zoom: 5.5,
      // });
      setViewport(DEFAULT_VIEWPORT);
    }
  }, [location.pathname]);

  // Currently catering Omics with parameter filter
  const addSamplingLocationObservations = (samplingLocations) => {
    const locations = [...samplingLocations];

    const retLocations = [];

    Object.keys(obsDatasetsLatest).forEach((key) => {
      const index = samplingLocations.findIndex((p) => p.properties.obs_dataset_id === obsDatasetsLatest[key].obs_dataset_id);
      if (index >= 0) {
        if (obsDatasetsLatest[key].observations[0].value.toFixed(2) !== '0.00') {
          locations[index].properties.observations = obsDatasetsLatest[key].observations;
          retLocations.push(locations[index]);
        }
      }
    });
    return retLocations;
  };

  const resetSamplingLocationObservations = (samplingLocations) => samplingLocations.map((s) => ({
    ...s,
    properties: {
      ...s.properties,
      observations: undefined,
    },
  }));

  const setMapData = () => {
    let filteredPlatforms = observationalPlatforms;
    if (observationalPlatforms && observationalPlatforms.length > 0 && datasetSummaries?.length > 0 && obsData) {
      if (filterFavorites && favoritePlatformData) {
        const favoriteIds = favoritePlatformData.map((o) => o.obs_dataset_id);
        // filter out platforms that are not in favorites
        filteredPlatforms = observationalPlatforms.filter((o) => favoriteIds.includes(o.properties.obs_dataset_id));
      }

      // filter out sampling_locations
      const continuousMonitoringPlatforms = (
        platformGroupings.buoy.join(
          platformGroupings.tower,
        )
      );
      const buoys = filteredPlatforms.filter((o) => continuousMonitoringPlatforms.includes(o.properties?.platform_type));
      let samplingLocations = filteredPlatforms
        .filter((o) => platformGroupings.sampling_location.includes(o.properties?.platform_type));

      if (selectedOverlay !== 'omics' || omicsFilters.filter === 'All') {
        samplingLocations = resetSamplingLocationObservations(samplingLocations);
      } else if (obsDatasetsLatest && omicsFilters.filter) {
        samplingLocations = addSamplingLocationObservations(samplingLocations);
      }

      // add external datasets (for now, NOAA CO-OPS water level stations) to buoys
      // suppress when favorites filter is active — ext_obs platforms have no obs_dataset_id and cannot be favorited
      // external platforms are continuous-monitoring stations like buoys/towers, so only
      // show them when the Anchored and/or Towers platform filters are selected
      const continuousMonitoringTagSelected = selectedTags?.some((t) => t.key === 'buoy' || t.key === 'tower');
      const showExternal = continuousMonitoringTagSelected && (brandingConfig.filters.isNest
        ? selectedTags?.some((t) => t.key === 'seagull')
        : !filterFavorites);
      if (showExternal && externalObsDatasets && externalObsDatasets.length > 0) {
        const lakeFilteredExternal = selectedLake
          ? externalObsDatasets.filter((p) => p.body_of_water === `lake-${selectedLake.toLowerCase()}`)
          : externalObsDatasets;
        buoys.push(...lakeFilteredExternal);
      }

      setBuoyData(buoys.map((platform) => {
        if (platform.obs_dataset_type === 'ext_obs') {
          let lastUpdatedInHours = Infinity;

          if (externalObsLatest?.length > 0) {
            const extObs = externalObsLatest.find(
              (o) => o.ext_obs_dataset_id === platform.ext_obs_dataset_id,
            );

            if (extObs && extObs.parameters?.length > 0) {
              const allTimestamps = extObs.parameters.flatMap((p) => p.observations?.map((obs) => obs.timestamp) || []);

              if (allTimestamps.length > 0) {
                const latestTimestamp = Math.max(...allTimestamps);
                lastUpdatedInHours = moment().diff(
                  moment.unix(latestTimestamp),
                  'hours',
                );
              }
            }
          }

          const platformEvent = lastUpdatedInHours <= appConfig.oldDataCutoffInHours ? 'activated' : 'unavailable';
          return ({
            ...platform,
            geometry: {
              coordinates: [platform.longitude, platform.latitude],
              type: 'Point',
            },
            properties: {
              ext_obs_dataset_id: platform.ext_obs_dataset_id,
              cluster: false,
              organization_id: platform?.organization_id,
              platform_name: platform?.platform_name,
              org_platform_id: platform?.source_id,
              platform_event: platformEvent,
              last_updated_parameter_in_hours: lastUpdatedInHours,
              platform_type: platform?.platform_type || 'tower',
            },
          });
        }

        // get organization_id from obs-dataset-summaries to add to properties
        const organizationId = datasetSummaries.find((d) => d.obs_dataset_id === platform.properties.obs_dataset_id)?.organization_id;
        return ({
          ...platform,
          properties: {
            ...platform.properties,
            cluster: false,
            organization_id: organizationId,
          },
        });
      }));

      setSamplingLocationData(samplingLocations.map((platform) => ({
        ...platform,
        properties: {
          ...platform.properties,
          platform_id: datasetSummaries.find((d) => platform.properties.obs_dataset_id === d.obs_dataset_id).obs_dataset_platform_assignment.platform_id,
          cluster: false,
        },
      })));
    } else {
      setBuoyData([]);
      setSamplingLocationData([]);
    }

    const selectedLakeChanged = prevSelectedLakeRef.current !== selectedLake;
    prevSelectedLakeRef.current = selectedLake;

    // When the lake selection changes, arm the pending snap. Ontario snaps
    // immediately because its viewport is hardcoded and needs no platform data.
    // All other lakes defer one cycle so that filterPlatforms in the dashboard
    // has time to update observationalPlatforms to the lake-filtered set before
    // we calculate bounds — otherwise we'd fit bounds over every Great Lake.
    if (selectedLakeChanged) {
      if (selectedLake === 'Ontario') {
        lakePendingSnapRef.current = false;
        setViewport({ latitude: 43.651070, longitude: -79.347015, zoom: 7 });
      } else {
        lakePendingSnapRef.current = selectedLake !== '';
      }
    }

    if (selectedLake === '' && location.pathname !== '/landing' && !selectedPlatform) {
      const urlSearchQuery = new URLSearchParams(location.search);
      if (urlSearchQuery.get('coords')) {
        const [lng, lat, zoom] = urlSearchQuery.get('coords')
          .split(',');
        setViewport({
          latitude: lat ? Number(lat) : 44.149,
          longitude: lng ? Number(lng) : -84.131259,
          zoom: zoom ? Number(zoom) : viewport.zoom,
        });
      }
    } else if (selectedLake !== '' && !selectedPlatform && lakePendingSnapRef.current && !selectedLakeChanged) {
      // This branch fires on the render after the lake changed, when
      // observationalPlatforms has been filtered to the selected lake.
      lakePendingSnapRef.current = false;
      if (filteredPlatforms && filteredPlatforms.length > 0) {
        const points = filteredPlatforms.map((item) => item.geometry).filter((mitem) => mitem !== undefined);
        const bounds = getBoundsForPoints(points);
        if (bounds) {
          setViewport(bounds);
        } else {
          setViewport(DEFAULT_VIEWPORT);
        }
      }
    }
  };

  useEffect(() => {
    if (selectedOverlay !== 'omics') {
      const updatedSamplingLocationData = resetSamplingLocationObservations(samplingLocationData);
      setSamplingLocationData(updatedSamplingLocationData);
    }
  }, [selectedOverlay]);

  useEffect(() => {
    setMapData();
  }, [observationalPlatforms, selectedLake, favoritePlatformData, filterFavorites, datasetSummaries]);

  useEffect(() => {
    if (selectedOverlay === 'omics' && obsDatasetsLatest && omicsFilters.filter) {
      if (omicsFilters.filter.toLowerCase() === 'all') {
        const updatedSamplingLocationData = resetSamplingLocationObservations(samplingLocationData);
        setSamplingLocationData(updatedSamplingLocationData);
      } else {
        const samplingLocations = [...samplingLocationData];

        setSamplingLocationData(addSamplingLocationObservations(samplingLocations));
      }
    }
  }, [obsDatasetsLatest]);

  useEffect(() => {
    const urlSearchQuery = new URLSearchParams(location.search);
    const queryPlatformId = urlSearchQuery.get('platform');
    if (urlSearchQuery.get('coords')) {
      const [lng, lat, zoom] = urlSearchQuery.get('coords')
        .split(',');
      if (Number(lng)
        .toFixed(7) !== viewport.longitude.toFixed(7)
        && Number(lat)
          .toFixed(7) !== viewport.latitude.toFixed(7)) {
        setViewport({
          latitude: Number(lat),
          longitude: Number(lng),
          zoom: zoom ? Number(zoom) : viewport.zoom,
        });
      }
    }

    if (observationalPlatforms
      && observationalPlatforms.length > 0
      && queryPlatformId
      && selectedPlatform?.properties?.org_platform_id !== queryPlatformId
    ) {
      setSelectedPlatform(observationalPlatforms.find((o) => o.properties?.org_platform_id === queryPlatformId));
    }
  }, [location.search]);

  const removeCanvas = (currentType) => {
    const lakes = ['leofs', 'lhofs', 'lmofs', 'lsofs', 'loofs'];
    for (let i = 0; i < lakes.length; i++) {
      const el = document.getElementById(`${lakes[i]}-${currentType}`);
      if (el) {
        el.remove();
      }
    }
  };

  useEffect(() => {
    // Lake selection and forecast toggling
    if (selectedOverlay === 'water_current') {
      removeCanvas('wind');
      removeCanvas('waves-500m');
    } else if (selectedOverlay === 'surface_water_temperature') {
      removeCanvas('water');
      removeCanvas('wind');
      removeCanvas('waves-500m');
    } else if (selectedOverlay === 'wave_height') {
      removeCanvas('water');
      removeCanvas('wind');
    } else if (selectedOverlay === 'wind_current') {
      removeCanvas('water');
      removeCanvas('waves-500m');
    } else if (selectedOverlay === 'omics') {
      removeCanvas('water');
      removeCanvas('wind');
      removeCanvas('waves-500m');
    } else {
      removeCanvas('water');
      removeCanvas('wind');
      removeCanvas('waves-500m');
    }
    if (selectedOverlay.length === 0 || selectedOverlay === 'omics') {
      setClickableContourLayers([]);
    } else if (selectedOverlay === 'bathy') {
      setClickableContourLayers([`CSBHexMapLayer${hexRes}`]);
    } else if (selectedLake.length > 0) {
      if (selectedOverlay === 'wave_height') {
        setClickableContourLayers(['primaryLake', 'primaryLakePeriod']);
      } else {
        setClickableContourLayers(['primaryLake', 'primaryLakePeriod']);
      }
    } else if (selectedLake.length === 0) {
      if (selectedOverlay === 'wave_height') {
        setClickableContourLayers(
          [
            'primaryLake',
            'lhofs',
            'lmofs',
            'loofs',
            'lsofs',
            'primaryLakePeriod',
            'lhofsPeriod',
            'lmofsPeriod',
            'loofsPeriod',
            'lsofsPeriod',
          ],
        );
      } else {
        setClickableContourLayers(['primaryLake', 'lhofs', 'lmofs', 'loofs', 'lsofs']);
      }
    }
    return () => {
    };
  }, [selectedTags, selectedLake, selectedOverlay, location.pathname, viewport]);

  const getNearestPlatform = (dataset, pos) => {
    let nearest;

    const obsAssetsData = observationalPlatforms.map((asset) => ({
      ...asset,
      distance: asset.geometry.coordinates[0] - dataset.geometry.coordinates[0],
    }));
    const obsAssetsDataSorted = obsAssetsData.slice()
      .sort((a, b) => a.distance - b.distance);

    // iterate over objects and calculate distance between them
    for (let i = 0; i < obsAssetsDataSorted.length; i += 1) {
      if (obsAssetsDataSorted[i].properties.org_platform_id === dataset.properties.org_platform_id) {
        if (pos === 'prev') {
          const prevKey = i === 0 ? obsAssetsDataSorted.length - 1 : i - 1;
          nearest = obsAssetsDataSorted[prevKey];
        } else if (pos === 'next') {
          const nextKey = i + 1;
          nearest = nextKey === obsAssetsDataSorted.length ? obsAssetsDataSorted[0] : obsAssetsDataSorted[nextKey];
        }
      }
    }
    return nearest;
  };

  const getParametersData = (dataset) => {
    if (dataset) {
      // navigate to marker
      const [longitude, latitude] = dataset.geometry.coordinates;

      setViewport({
        latitude,
        longitude,
        zoom: viewport.zoom,
      });

      const parameterData = getParameters({
        dataset: dataset.properties, datasetSummaries, obsData, parameterConfigurations, registeredParameters, unitPreferences,
      });

      setParameters(parameterData);

      // Get prev and next platforms
      const prevPlatform = getNearestPlatform(dataset, 'prev');
      const nextPlatform = getNearestPlatform(dataset, 'next');

      setPlatforms([prevPlatform, nextPlatform]);

      // set current dataset
      setSelectedPlatform(dataset);
    }
  };

  const triggerTooltip = useCallback((dataset) => {
    setTooltipInfo(dataset);
    setContourInfo(null);

    if (dataset) {
      // TODO: put obs_dataset_type in an enum
      if (dataset.obs_dataset_type === 'ext_obs') {
        const externalParameterObs = getExternalParameterObservations(
          dataset,
          externalParameters,
          externalObsLatest,
          parameterConfigurations,
          unitPreferences,
        );

        setParameters(externalParameterObs);
      } else {
        const parameterData = getParameters({
          dataset: dataset.properties, datasetSummaries, obsData, parameterConfigurations, registeredParameters, unitPreferences,
        });

        setParameters(parameterData);
      }
    }
  }, [datasetSummaries, obsData, parameterConfigurations, registeredParameters, unitPreferences]);

  const DEBOUNCE_MS = 200;

  // Debounce once per render
  const handleAction = React.useMemo(() => _.debounce(triggerTooltip, DEBOUNCE_MS), [triggerTooltip]);

  // Cleanup to avoid memory leaks
  useEffect(() => () => {
    handleAction.cancel();
  }, [handleAction]);

  const handleClick = (dataset) => {
    setSelectedPlatform(dataset);
    setShowDetail(true);
    setTooltipInfo(null);

    // Get prev and next platforms
    const prevPlatform = getNearestPlatform(dataset, 'prev');
    const nextPlatform = getNearestPlatform(dataset, 'next');

    setPlatforms([prevPlatform, nextPlatform]);
    if (dataset) {
      if (dataset.obs_dataset_type === 'ext_obs') {
        const externalParameterObs = getExternalParameterObservations(
          dataset,
          externalParameters,
          externalObsLatest,
          parameterConfigurations,
          unitPreferences,
        );

        setParameters(externalParameterObs);
      } else if (platformGroupings.sampling_location.includes(dataset.properties.platform_type)) {
        // Fetch this dataset's grab-sample data on demand, rather than depending on
        // obsDatasetsLatest/latest_observations, which grab-sample (Omics/GLAMR) data
        // doesn't reliably sync into.
        getSamplingData({
          startDate: '2010-01-01',
          endDate: new Date().toISOString().slice(0, 10),
          obsDatasetId: dataset.properties.obs_dataset_id,
        }).then((result) => {
          const targetObsDataset = result?.[0];
          const sampleObsData = [{
            obs_dataset_id: dataset.properties.obs_dataset_id,
            parameters: (targetObsDataset?.parameters || []).map((param) => {
              // observations are ordered latest-first, so [0] is the most recent sample
              const latestObs = param.observations?.[0];
              return {
                parameter_id: param.parameter_id,
                observations: latestObs ? [
                  {
                    qartod: 9,
                    // sampling_data timestamps are naive ISO strings representing UTC
                    timestamp: new Date(`${latestObs.timestamp}Z`).getTime() / 1000,
                    value: latestObs.value,
                  },
                ] : [],
              };
            }),
          }];
          const sampleParameterData = getParameters({
            dataset: dataset.properties, datasetSummaries, obsData: sampleObsData, parameterConfigurations, registeredParameters, unitPreferences,
          });
          setParameters(sampleParameterData);
        });
      } else {
        const parameterData = getParameters({
          dataset: dataset.properties, datasetSummaries, obsData, parameterConfigurations, registeredParameters, unitPreferences,
        });
        setParameters(parameterData);
      }
    }
    ReactGA.event(PlatformMarkerEvent.MarkerClicked);
    ReactGA.event({
      ...PlatformMarkerEvent.SelectedPlatformId,
      label: `${dataset.properties.org_platform_id} Clicked on Map for Snapshot`,
    });
  };

  const getOrganizationName = React.useMemo(() => (dataset) => {
    if (dataset?.ext_obs_dataset_id) {
      const org = organizations?.find(((o) => o.organization_id === dataset?.organization_id));
      return org?.name;
    }

    const datasetRecord = datasets.filter((o) => o.org_platform_id === dataset.org_platform_id);
    if (datasetRecord?.length === 0) {
      return '';
    }
    const organization = organizations.filter((o) => o.organization_id === datasetRecord[0].organization_id);

    return organization[0].name;
  }, [datasets, organizations]);

  // get map bounds
  const bounds = mapRef.current
    ? mapRef.current
      .getMap()
      .getBounds()
      .toArray()
      .flat()
    : null;

  const isFavorite = (datasetId) => {
    if (favoritePlatformData) {
      if (favoritePlatformData.msg && favoritePlatformData.msg === 'Signature verification failed') {
        console.log('WARNING: login signature failed');
        return false;
      }
      const foundFavorite = favoritePlatformData?.find((p) => p.obs_dataset_id === datasetId);
      if (foundFavorite) {
        return true;
      }
    }

    return false;
  };

  // get clusters
  const {
    clusters: continuousMonitoringClusters,
    supercluster: continuousMonitoringSuperCluster,
  } = useSupercluster({
    points: buoyData,
    bounds,
    zoom: viewport.zoom,
    options: {
      radius: 40,
      maxZoom: 20,
      map: (props) => ({ ...props }),
      reduce: (acc, p) => {
        // https://github.com/leighhalliday/use-supercluster#map--reduce-options
        // with reduce we only have access to the accumulator and current - not sure of a cleaner way to handle this and get the right result
        if (p.platform_event === 'decommissioned'
          && acc.platform_event !== 'recovered'
          && acc.platform_event !== 'temporarily_recovered'
          && acc.platform_event !== 'deactivated'
          && acc.platform_event !== 'decommissioned'
          && acc.platform_event !== 'activated') {
          acc.platform_event = 'decommissioned';
        }

        if (p.platform_event === 'recovered'
          && acc.platform_event !== 'temporarily_recovered'
          && acc.platform_event !== 'deactivated'
          && acc.platform_event !== 'decommissioned'
          && acc.platform_event !== 'activated') {
          acc.platform_event = 'recovered';
        }

        if (p.platform_event === 'temporarily_recovered'
          && acc.platform_event !== 'deactivated'
          && acc.platform_event !== 'decommissioned'
          && acc.platform_event !== 'activated') {
          acc.platform_event = 'temporarily_recovered';
        }

        if (p.platform_event === 'deactivated'
          && acc.platform_event !== 'decommissioned'
          && acc.platform_event !== 'activated') {
          acc.platform_event = 'deactivated';
        }

        if (p.platform_event === 'decommissioned'
          && acc.platform_event !== 'activated') {
          acc.platform_event = 'decommissioned';
        }

        if (p.platform_event === 'unavailable'
          && acc.platform_event !== 'activated') {
          acc.platform_event = 'unavailable';
        }
        if (p.platform_event === 'activated') {
          acc.platform_event = 'activated';
        }
        if (
          p.platform_event === 'activated'
          && p.last_updated_parameter_in_hours < appConfig.oldDataCutoffInHours
        ) {
          acc.platform_event = 'activated';
          acc.last_updated_parameter_in_hours = p.last_updated_parameter_in_hours;
        }

        // if any platform is a favorite, make the cluster show this
        if (isFavorite(p.obs_dataset_id)) {
          acc.is_favorite = true;
        }

        return acc;
      },
    },
  });

  // get sampling location clusters
  const {
    clusters: samplingLocationClusters,
    supercluster: samplingLocationSupercluster,
  } = useSupercluster({
    points: samplingLocationData,
    bounds,
    zoom: viewport.zoom,
    options: {
      radius: 40,
      maxZoom: 20,
      map: (props) => ({ ...props }),
      reduce: (acc) => {
        acc.platform_type = 'sampling_location';
        return acc;
      },
    },
  });

  const handleClusterClick = (cluster, latitude, longitude) => {
    const expansionZoom = Math.min(
      continuousMonitoringSuperCluster.getClusterExpansionZoom(cluster.id),
      20,
    );

    const map = mapRef.current.getMap();

    map.flyTo({
      center: [longitude, latitude],
      zoom: expansionZoom,
      duration: 900,
      essential: true,
    });

    // setViewport({
    //   ...viewport,
    //   latitude,
    //   longitude,
    //   zoom: expansionZoom,
    //   transitionDuration: 'auto',
    // });
    ReactGA.event(PlatformMarkerEvent.ClusterClicked);
  };

  const handleSamplingLocationClusterClick = (cluster, latitude, longitude) => {
    const expansionZoom = Math.min(
      samplingLocationSupercluster.getClusterExpansionZoom(cluster.id),
      20,
    );

    setViewport({
      ...viewport,
      latitude,
      longitude,
      zoom: expansionZoom,
      // transitionInterpolator: new FlyToInterpolator({
      //   speed: 2,
      // }),
      transitionDuration: 'auto',
    });
    ReactGA.event(PlatformMarkerEvent.ClusterClicked);
  };

  const samplingLocationClustersContent = React.useMemo(() => (
    <Cluster
      mapData={samplingLocationClusters}
      showDetail={showDetail}
      selectedPlatform={selectedPlatform}
      handleAction={handleAction}
      handleClick={handleClick}
      handleClusterClick={handleSamplingLocationClusterClick}
    />
  ), [samplingLocationClusters, samplingLocationData, selectedPlatform, showDetail]);

  const continuousMonitoringClustersContent = React.useMemo(() => (
    <Cluster
      mapData={continuousMonitoringClusters}
      showDetail={showDetail}
      selectedPlatform={selectedPlatform}
      handleAction={handleAction}
      handleClick={handleClick}
      handleClusterClick={handleClusterClick}
    />
  ), [continuousMonitoringClusters, buoyData, selectedPlatform, showDetail]);

  if (selectedPlatform && !showDetail) {
    handleClick(selectedPlatform);
  }

  const handleShowDetail = (status) => {
    setShowDetail(status);
    setSelectedPlatform(null);
  };

  const onContourClick = useCallback((event) => {
    setTooltipInfo(null);

    if (!event.features) {
      return;
    }

    if (clickableContourLayers) {
      const {
        features,
      } = event;
      if (selectedOverlay === 'bathy') {
        const tempHexSurveys = [];
        let tempHexSurveyCount = 0;
        const clickedFeatures = features && features;
        for (let i = 0; i < clickedFeatures.length; i++) {
          const thisFeature = clickedFeatures[i];
          if (thisFeature.properties.survey) {
            tempHexSurveyCount++;
            if (!tempHexSurveys.find((srv) => srv === thisFeature.properties.survey)) {
              tempHexSurveys.push(thisFeature.properties.survey);
            }
          }
        }
        setClickedHexLoc(
          {
            clickLon: event.lngLat.lng,
            clickLat: event.lngLat.lat,
          },
        );
        setHexSurveyCount(tempHexSurveyCount);
        setHexSurveys(tempHexSurveys);
      } else {
        setHexSurveyCount(0);
        setHexSurveys([]);
        const clickedFeature = features && features[0];
        setContourInfo(
          clickedFeature
            ? {
              feature: clickedFeature,
              clickLon: event.lngLat.lng,
              clickLat: event.lngLat.lat,
              ts: performance.now(),
            }
            : null,
        );
        if (selectedOverlay === 'wave_height') {
          // Expecting 2 features
          const clickedFeature = features.filter((f) => f.layer.source.indexOf('Period') === -1);
          const clickedPeriodFeature = features.filter((f) => f.layer.source.indexOf('Period') !== -1);
          setContourInfo(
            clickedFeature.length > 0 && clickedPeriodFeature.length > 0
              ? {
                feature: clickedFeature[0],
                periodFeature: clickedPeriodFeature[0],
                clickLon: event.lngLat.lng,
                clickLat: event.lngLat.lat,
                ts: performance.now(),
              }
              : null,
          );
        } else {
          const clickedFeature = features && features[0];
          setContourInfo(
            clickedFeature
              ? {
                feature: clickedFeature,
                clickLon: event.lngLat.lng,
                clickLat: event.lngLat.lat,
                ts: performance.now(),
              }
              : null,
          );
        }
      }
    }
  }, [selectedOverlay]);

  useEffect(() => {
    if (contourInfo) {
      setContourInfo(null);
    }

    if (hexSurveyCount > 0) {
      setHexSurveyCount(0);
      setHexSurveys([]);
    }
  }, [viewport]);

  // Listen for changes in storage
  useEffect(() => {
    function nullifyContourInfo() {
      setContourInfo(null);
    }

    window.addEventListener('storage', nullifyContourInfo);

    return () => {
      window.removeEventListener('storage', nullifyContourInfo);
    };
  }, []);

  useEffect(() => {
    if (location.search) {
      ReactGA.event({
        ...MapFilterEvent.MapFilterParams,
        label: location.search,
      });
    }
  }, [location.search]);

  // Removes data older than 24 hours from parameters in tooltip
  const filteredParameters = parameters?.filter((parameter) => {
    const currentDate = new Date();
    const parameterTimestamp = new Date(parameter.date);
    const timeDifference = currentDate - parameterTimestamp;
    const millisecondsIn24Hours = 86400000;

    if (timeDifference > millisecondsIn24Hours) {
      return false;
    }
    return true;
  });

  const espParameters = parameters.filter((parameter) => espStandardNames.indexOf(parameter.standard_name) !== -1);

  const closeShareModal = () => {
    setShowShareModal(false);

    if (!hasUserSeenShareModal && authStatus === 'unauthenticated') {
      sessionStorage.setItem('hasUserSeenShareModal', true);
      setShowSignupModal(true);
    }
  };

  const EPS = 1e-7;
  const pick = (vs) => ({
    longitude: vs.longitude,
    latitude: vs.latitude,
    zoom: vs.zoom,
    bearing: vs.bearing ?? 0,
    pitch: vs.pitch ?? 0,
  });
  const equalVS = (a, b) => Math.abs(a.longitude - b.longitude) < EPS
    && Math.abs(a.latitude - b.latitude) < EPS
    && Math.abs(a.zoom - b.zoom) < EPS
    && (a.bearing ?? 0) === (b.bearing ?? 0)
    && (a.pitch ?? 0) === (b.pitch ?? 0);

  // Throttle onMove to once per animation frame
  const raf = useRef(0);
  const pending = useRef(viewport);

  const handleMove = useCallback((event) => {
    const next = pick(event.viewState);
    if (equalVS(pending.current, next)) return; // ignore no-op changes
    pending.current = next;

    if (!raf.current) {
      raf.current = requestAnimationFrame(() => {
        setViewport((prev) => (equalVS(prev, pending.current) ? prev : pending.current));
        raf.current = 0;
      });
    }
  }, []);

  return (
    <>
      <CustomModal
        visible={showShareModal}
        onCancel={closeShareModal}
        footer={null}
        isSocialShare
        className="socialShare"
      >
        <MapShareModalContent
          key={modalKey}
          selectedOverlay={selectedOverlay}
          initialViewport={viewport}
          UTCPath={activeUTCPath}
          selectedLake={selectedLake}
          tagsList={tagsList}
          onTagSelect={onTagSelect}
          onCancel={closeShareModal}
        />
      </CustomModal>
      <CustomModal
        visible={showSignupModal}
        onCancel={() => setShowSignupModal(false)}
        footer={null}
        width="700px"
        styles={{ backgroundColor: 'black', borderRadius: 25 }}
        className="socialShare"
        isSocialShare
      >
        <CommunitySignupModal handleCommunityModalSignup={setShowSignupModal} />
      </CustomModal>
      {!isMobile && (
        <MapOverlayDrawer
          onTagsSelect={onTagSelect}
          tagsList={tagsList}
          selectedOverlay={selectedOverlay}
          omicsFilters={omicsFilters}
          setOmicsFilters={setOmicsFilters}
        />
      )}
      {!filterVisible && showDetail && parameters && platforms && selectedPlatform && (
        <DatasetDetail
          dataset={selectedPlatform}
          parameters={parameters}
          platforms={platforms}
          visible={showDetail}
          getParameters={getParametersData}
          obsData={obsData}
          handleShowDetail={handleShowDetail}
          getOrganizationName={getOrganizationName}
        />
      )}
      <Map
        viewState={viewport}
        onMove={handleMove}
        style={{ width: '100%', height: '100%' }}
        className="custom-map"
        maxZoom={20}
        maxPitch={0}
        dragRotate={false}
        touchPitch={false}
        mapStyle={mapStyle}
        interactiveLayerIds={clickableContourLayers}
        onClick={onContourClick}
        mapboxAccessToken={appConfig.mapboxAccessToken}
        ref={mapRef}
        onLoad={(e) => {
          const map = e.target;
          map.touchZoomRotate.disableRotation();
        }}
      >
        {showWebcams && (brandingConfig.filters.isNest ? selectedTags?.some((t) => t.key === 'seagull') : !filterFavorites) && <WebCams selectedLake={selectedLake} />}
        {(selectedTags?.some((t) => t.key === 'moving') && datasetTrajectories?.features?.length > 1) && (
          datasetTrajectories.features.map((trajectory, index) => (
            <Trajectory
              trajectory={trajectory}
              key={index}
              showPaths={showPaths}
            />
          ))
        )}

        {continuousMonitoringClustersContent}
        {/* {featureFlag.omics && (
          <CollectionSites
            observationalPlatforms={observationalPlatforms}
            viewport={viewport}
            setViewport={setViewport}
            bounds={bounds}
          />
        )} */}

        {featureFlag.omics && samplingLocationClustersContent}

        {location.pathname !== '/landing' && tooltipInfo && tooltipInfo?.properties.platform_type === 'sampling_location' && !isMobile && (
          <Popup
            key={`omics-${tooltipInfo.properties.org_platform_id}`}
            tipSize={10}
            longitude={tooltipInfo.geometry.coordinates[0]}
            latitude={tooltipInfo.geometry.coordinates[1]}
            closeOnClick={false}
            closeButton={false}
            onClose={setTooltipInfo}
            className="omics-popup"
          >
            <OmicsDatasetSummary
              info={tooltipInfo}
              filters={omicsFilters}
              parameters={registeredParameters}
            />
          </Popup>
        )}
        {location.pathname !== '/landing' && tooltipInfo && tooltipInfo?.properties?.platform?.external_id_type === 'esp_site' && !isMobile && (
          <Popup
            key={`omics-${tooltipInfo.properties.org_platform_id}`}
            tipSize={10}
            longitude={tooltipInfo.geometry.coordinates[0]}
            latitude={tooltipInfo.geometry.coordinates[1]}
            closeOnClick={false}
            closeButton={false}
            onClose={setTooltipInfo}
          >
            <ESPDatasetSummary
              info={tooltipInfo}
              parameters={espParameters}
              obsData={obsData}
            />
          </Popup>
        )}
        {
          location.pathname !== '/landing'
          && tooltipInfo
          && tooltipInfo?.properties?.ext_obs_dataset_id
          && (
            <Popup
              tipSize={10}
              longitude={tooltipInfo.geometry.coordinates[0]}
              latitude={tooltipInfo.geometry.coordinates[1]}
              closeOnClick={false}
              closeButton={false}
              onClose={setTooltipInfo}
              className="dataset-summary-popup"
            >
              <ExternalDatasetSummary
                info={tooltipInfo}
                parameters={filteredParameters}
                obsLatestData={externalObsLatest}
              />
            </Popup>
          )
        }
        {
          location.pathname !== '/landing'
          && tooltipInfo
          && tooltipInfo?.properties.platform_type !== 'sampling_location'
          && tooltipInfo?.properties?.platform?.external_id_type !== 'esp_site'
          && !isMobile
          && !tooltipInfo?.properties?.ext_obs_dataset_id
          && (
            <Popup
              key={`dataset-${tooltipInfo.properties.org_platform_id}`}
              tipSize={10}
              longitude={tooltipInfo.geometry.coordinates[0]}
              latitude={tooltipInfo.geometry.coordinates[1]}
              closeOnClick={false}
              closeButton={false}
              onClose={setTooltipInfo}
              className="dataset-summary-popup"
            >
              <DatasetSummary
                info={tooltipInfo}
                parameters={filteredParameters}
                obsData={obsData}
              />
            </Popup>
          )
        }
        {selectedOverlay === 'surface_water_temperature'
          && (
            <SurfaceWaterTemperature
              UTCPath={activeUTCPath}
              selectedLake={selectedLake}
            />
          )}
        {selectedOverlay === 'surface_water_temperature' && contourInfo && !showDetail
          && (
            <Popup
              key={`${contourInfo.clickLon},${contourInfo.clickLat}`}
              longitude={contourInfo.clickLon}
              latitude={contourInfo.clickLat}
              closeOnClick
              closeOnMove={false}
              closeButton={false}
              onClose={() => setContourInfo(null)}
            >
              <div className="contour-popup-title">
                {preferredTemperatureUnit(contourInfo.feature.properties.level)}
              </div>
              <div className="contour-popup-text">
                {getDms(contourInfo.clickLat, contourInfo.clickLon)}
              </div>
            </Popup>
          )}
        {hexSurveyCount > 0
          && (
            <Popup
              key={`${clickedHexLoc.clickLon},${clickedHexLoc.clickLat}`}
              longitude={clickedHexLoc.clickLon}
              latitude={clickedHexLoc.clickLat}
              closeOnClick
              closeButton={false}
            >
              <div className="bathy-popup-text">
                <strong>
                  Total Surveys:
                  {' '}
                  {hexSurveyCount.toString()}
                </strong>
                <br />
                <strong>
                  Unique Surveys:
                </strong>
                <br />
                <ul>
                  {hexSurveys.map((hs) => (
                    <li>{hs}</li>
                  ))}
                </ul>
              </div>
            </Popup>
          )}
        <div id="currents" />
        {selectedOverlay === 'wave_height'
          && (
            <>
              <WaveMagnitude
                UTCPath={activeUTCPath}
                selectedLake={selectedLake}
              />
              <WavePeriod
                UTCPath={activeUTCPath}
                selectedLake={selectedLake}
              />
            </>
          )}
        {selectedOverlay === 'wave_height'
          && (
            <CurrentAnimation
              urlCoords={urlCoords}
              viewport={viewport}
              mapRef={mapRef}
              selectedLake={selectedLake}
              selectedOverlay={selectedOverlay}
              UTCPath={activeUTCPath}
              isMobile={isMobile}
              location={location}
              particleCount={2500}
            />
          )}
        {selectedOverlay === 'wave_height' && contourInfo && !showDetail
          && (
            <Popup
              longitude={contourInfo.clickLon}
              latitude={contourInfo.clickLat}
              closeOnClick
              closeButton={false}
            >
              <div className="contour-popup-title">
                {
                  `${preferredWaveHeightUnit(contourInfo?.feature?.properties.level)} ${contourInfo?.periodFeature?.properties.z_min}s`
                }
              </div>
              <div className="contour-popup-text">
                {getDms(contourInfo.clickLat, contourInfo.clickLon)}
              </div>
            </Popup>
          )}

        {selectedOverlay === 'water_current'
          && (
            <WaterMagnitude
              UTCPath={activeUTCPath}
              selectedLake={selectedLake}
            />
          )}
        {selectedOverlay === 'water_current'
          && (
            <CurrentAnimation
              urlCoords={urlCoords}
              viewport={viewport}
              mapRef={mapRef}
              selectedLake={selectedLake}
              selectedOverlay={selectedOverlay}
              UTCPath={activeUTCPath}
              isMobile={isMobile}
              location={location}
            />
          )}
        {selectedOverlay === 'water_current' && contourInfo && !showDetail
          && (
            <Popup
              key={`${contourInfo.clickLon},${contourInfo.clickLat}`}
              longitude={contourInfo.clickLon}
              latitude={contourInfo.clickLat}
              closeOnClick
              closeButton={false}
            >
              <div className="contour-popup-title">
                {preferredWaterMagnitudeUnit(contourInfo.feature.properties.level)}
              </div>
              <div className="contour-popup-text">
                {getDms(contourInfo.clickLat, contourInfo.clickLon)}
              </div>
            </Popup>
          )}

        {selectedOverlay === 'wind_current'
          && (
            <WindMagnitude
              UTCPath={activeUTCPath}
              selectedLake={selectedLake}
            />
          )}
        {selectedOverlay === 'wind_current' && contourInfo && !showDetail
          && (
            <Popup
              key={`${contourInfo.clickLon},${contourInfo.clickLat}`}
              longitude={contourInfo.clickLon}
              latitude={contourInfo.clickLat}
              closeOnMove={false}
              closeOnClick
              closeButton={false}
            >
              <div className="contour-popup-title">
                {preferredWindMagnitudeUnit(contourInfo.feature.properties.level)}
              </div>
              <div className="contour-popup-text">
                {getDms(contourInfo.clickLat, contourInfo.clickLon)}
              </div>
            </Popup>
          )}
        {selectedOverlay === 'bathy'
          && (
            <CSBHexMap viewport={viewport} />
          )}
        {children}
      </Map>
      {selectedOverlay === 'wind_current'
        && (
          <CurrentAnimation
            urlCoords={urlCoords}
            viewport={viewport}
            mapRef={mapRef}
            selectedLake={selectedLake}
            selectedOverlay={selectedOverlay}
            UTCPath={activeUTCPath}
            isMobile={isMobile}
            location={location}
          />
        )}

      {['surface_water_temperature', 'water_current', 'wind_current', 'wave_height'].find((overlay) => overlay === selectedOverlay)
        && location.pathname !== '/landing' && (
        <TimeSlider
          onSliderChange={handleSliderChange}
          selectedOverlay={selectedOverlay}
        />
      )}

      {['omics'].find((overlay) => overlay === selectedOverlay.toLowerCase())
        && omicsFilters.filter
        && omicsFilters.startDate
        && omicsFilters.endDate
        && (
          <SelectedFilters
            filters={omicsFilters}
            type={selectedOverlay}
          />
        )}

    </>
  );
};

CustomMap.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.element),
    PropTypes.element.isRequired,
  ]),
  selectedLake: PropTypes.string,
  selectedOverlay: PropTypes.string,
  selectedTags: PropTypes.array,
  datasets: PropTypes.array,
  datasetSummaries: PropTypes.array,
  registeredParameters: PropTypes.array,
  observationalPlatforms: PropTypes.array,
  organizations: PropTypes.array,
  obsData: PropTypes.array,
  parameterConfigurations: PropTypes.array,
  filterVisible: PropTypes.bool,
  pushToHistory: PropTypes.func,
  onTagSelect: PropTypes.func,
  tagsList: PropTypes.object,
  filterFavorites: PropTypes.bool,
  favoritePlatformData: PropTypes.array,
  datasetTrajectories: PropTypes.object,
  showPaths: PropTypes.bool,
  omicsFilters: PropTypes.object,
  setOmicsFilters: PropTypes.func,
  obsDatasetsLatest: PropTypes.array,
  showShareModal: PropTypes.bool,
  setShowShareModal: PropTypes.func,
  externalObsDatasets: PropTypes.array,
  externalParameters: PropTypes.array,
  externalObsLatest: PropTypes.array,
  showWebcams: PropTypes.bool,
};

CustomMap.defaultProps = {
  children: null,
  selectedLake: '',
  selectedOverlay: '',
  selectedTags: [],
  datasets: [],
  datasetSummaries: [],
  registeredParameters: [],
  observationalPlatforms: [],
  organizations: [],
  obsData: [],
  parameterConfigurations: [],
  filterVisible: false,
  pushToHistory: () => {
  },
  onTagSelect: () => {
  },
  tagsList: {},
  filterFavorites: false,
  favoritePlatformData: [],
  datasetTrajectories: {},
  showPaths: true,
  omicsFilters: {},
  setOmicsFilters: () => {},
  obsDatasetsLatest: [],
  showShareModal: false,
  setShowShareModal: () => {},
  externalObsDatasets: [],
  externalParameters: [],
  externalObsLatest: [],
  showWebcams: true,
};

export default memo(CustomMap);
